import React, { useEffect, useState } from "react";
import { Button } from "@web-monorepo/mds";
import { logSparksEvent } from "@src/utils/sparks/logSparksEvent";
import { STRIPE_ANNUAL_HREF, STRIPE_MONTHLY_HREF } from "../shared/constants";
import { SparksPageView } from "../shared/SparksPageView";
import { isValidEmail, withPrefilledEmail } from "../shared/utils";

type PlanId = "annual" | "monthly";

const CTA_CLICKED_EVENT = "sparks.stripe.paywall.cta.clicked";

const logCtaClick = (planId: PlanId) => {
  void logSparksEvent({
    eventName: CTA_CLICKED_EVENT,
    eventValue: planId,
  });
};

const PLAN_SELECTED_ICON = "/images/sparks/paywall/plan-selected.svg";
const PLAN_UNSELECTED_ICON = "/images/sparks/paywall/plan-unselected.svg";

const plans = {
  annual: {
    id: "annual",
    label: "Annual",
    price: "$64.99",
    previousPrice: "$119.88/yr",
    compactPrice: "$5.42/MO",
    desktopSubtext: "$5.42/mo",
    cadence: "year",
    href: STRIPE_ANNUAL_HREF,
  },
  monthly: {
    id: "monthly",
    label: "Monthly",
    price: "$9.99",
    compactPrice: "$9.99/MO",
    cadence: "month",
    href: STRIPE_MONTHLY_HREF,
  },
} as const;

const PlanStatusIcon = ({ selected }: { selected: boolean }) => (
  <img
    src={selected ? PLAN_SELECTED_ICON : PLAN_UNSELECTED_ICON}
    alt=""
    aria-hidden="true"
    className="h-20 w-20 shrink-0"
  />
);

const MobilePlanOption = ({
  planId,
  selected,
  onSelect,
}: {
  planId: PlanId;
  selected: boolean;
  onSelect: () => void;
}) => {
  const plan = plans[planId];
  const tone = selected ? "text-brand-secondary" : "text-neutral-500";

  return (
    <div className="flex w-full flex-col items-start">
      {planId === "annual" && (
        <span className="ml-[19px] flex h-24 w-[98px] items-center justify-center rounded-t-2xs bg-brand-secondary px-sm text-[12px] font-bold uppercase tracking-[0.7px] text-[#fffae6]">
          SAVE 45%
        </span>
      )}
      <label
        className={`flex min-h-[66px] w-full cursor-pointer items-center justify-between rounded-lg border-[2.5px] bg-[#fffae6] px-[24px] py-sm transition-colors focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-primary ${
          selected ? "border-brand-secondary" : "border-neutral-500"
        }`}
      >
        <input
          type="radio"
          name="sparks-stripe-plan"
          value={plan.id}
          checked={selected}
          onChange={onSelect}
          className="sr-only"
        />
        <span className="flex min-w-0 flex-col items-start justify-center">
          <span className={`font-ginto text-[18px] font-bold leading-[1.3] ${tone}`}>{plan.label}</span>
          {planId === "annual" && (
            <span className="flex items-center gap-x-2xs font-ginto text-[16px] leading-[1.4]">
              <span className="text-neutral-500 line-through">{plans.annual.previousPrice}</span>
              <span className="text-brand-secondary">{plans.annual.price}</span>
            </span>
          )}
        </span>
        <span className="ml-sm flex shrink-0 items-center gap-x-sm">
          <span className={`font-ginto text-[16px] font-bold leading-[1.4] ${tone}`}>{plan.compactPrice}</span>
          <PlanStatusIcon selected={selected} />
        </span>
      </label>
    </div>
  );
};

type PricingProps = {
  annualHref: string;
  monthlyHref: string;
};

const MobilePricing = ({ annualHref, monthlyHref }: PricingProps) => {
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>("annual");
  const selectedPlan = plans[selectedPlanId];
  const selectedPlanHref = selectedPlanId === "annual" ? annualHref : monthlyHref;

  return (
    <div className="mt-[49px] flex w-full max-w-[321px] flex-col lg:hidden">
      <fieldset className="flex w-full flex-col gap-y-[12px]">
        <legend className="sr-only">Choose a Dojo Sparks plan</legend>
        <MobilePlanOption
          planId="annual"
          selected={selectedPlanId === "annual"}
          onSelect={() => setSelectedPlanId("annual")}
        />
        <MobilePlanOption
          planId="monthly"
          selected={selectedPlanId === "monthly"}
          onSelect={() => setSelectedPlanId("monthly")}
        />
      </fieldset>

      <Button
        as="a"
        href={selectedPlanHref}
        variant="primary"
        size="l"
        className="mt-[24px] w-full"
        data-testid="mobile-pricing-cta"
        onClick={() => logCtaClick(selectedPlanId)}
      >
        Start my free week
      </Button>
      <p className="mt-[15px] text-center font-ginto text-[16px] leading-[1.4] text-brand-secondary">
        7 day free trial, then just {selectedPlan.price} per {selectedPlan.cadence}
        <br />
        Cancel anytime in App Store
      </p>
    </div>
  );
};

const DesktopPricing = ({ annualHref, monthlyHref }: PricingProps) => (
  <div className="mt-[70px] hidden flex-col items-center lg:flex">
    <div className="flex items-center justify-center">
      <article className="relative z-0 mt-[19px] flex w-[392px] flex-col items-start gap-y-[37px] rounded-md border-[3px] border-brand-secondary bg-[#fffae6] px-[32px] py-[27px]">
        <div className="flex w-full flex-col items-start gap-y-[13px]">
          <h2 className="font-ginto text-[28px] font-bold leading-[1.3] text-brand-primary">{plans.monthly.label}</h2>
          <p className="flex h-[47px] items-center font-melun text-[55px] font-bold leading-none text-[#171a1e]">
            {plans.monthly.price}
          </p>
          <span className="invisible font-ginto text-[19px] leading-[1.3]" aria-hidden="true">
            spacer
          </span>
        </div>
        <Button as="a" href={monthlyHref} variant="primary" size="m" onClick={() => logCtaClick("monthly")}>
          Start my free week
        </Button>
      </article>

      <article className="relative z-10 -ml-[21px] flex w-[471px] flex-col items-start gap-y-[45px] overflow-hidden rounded-md border-[3px] border-brand-secondary bg-[#fffae6] px-[38px] py-[32px]">
        <span className="absolute right-[14px] top-[20px] flex h-[27px] items-center rounded-3xs bg-brand-secondary px-[7px] font-ginto text-[13px] font-bold uppercase leading-[1.2] text-white">
          SAVE 45%
        </span>
        <div className="flex w-full flex-col items-start gap-y-[15px]">
          <h2 className="font-ginto text-[28px] font-bold leading-[1.3] text-brand-primary">{plans.annual.label}</h2>
          <p className="font-melun text-[55px] font-bold leading-none text-[#171a1e]">{plans.annual.price}</p>
          <p className="font-ginto text-[19px] leading-[1.3] text-[#5d5c62]">{plans.annual.desktopSubtext}</p>
        </div>
        <Button as="a" href={annualHref} variant="primary" size="m" onClick={() => logCtaClick("annual")}>
          Start my free week
        </Button>
      </article>
    </div>

    <div className="mt-[41px] flex w-[333px] flex-col items-center gap-y-[10px] text-brand-secondary">
      <p className="font-melun text-[24px] font-medium leading-[1.2]">Select from one of our plans</p>
      <div className="flex items-center gap-x-lg font-ginto text-[16px] leading-[1.4]">
        <span className="flex items-center gap-x-[6px] whitespace-nowrap">
          <img src="/images/sparks/paywall/free-trial.svg" alt="" aria-hidden="true" className="h-24 w-24" />7 day free
          trial
        </span>
        <span className="flex items-center gap-x-[6px] whitespace-nowrap">
          <img src="/images/sparks/paywall/cancel-anytime.svg" alt="" aria-hidden="true" className="h-24 w-24" />
          Cancel anytime
        </span>
      </div>
    </div>
  </div>
);

export const SparksStripePricing = () => {
  const [email, setEmail] = useState("");

  useEffect(() => {
    const emailParam = new URLSearchParams(window.location.search).get("email");
    if (emailParam && isValidEmail(emailParam)) {
      setEmail(emailParam);
    }
  }, []);

  const annualHref = withPrefilledEmail(STRIPE_ANNUAL_HREF, email);
  const monthlyHref = withPrefilledEmail(STRIPE_MONTHLY_HREF, email);

  return (
    <main className="brand-sparks min-h-[100svh] w-full bg-sky-300 text-brand-secondary">
      <SparksPageView eventName="web.external_page.sparks_stripe_pricing.page_view" />
      <div className="mx-auto flex min-h-[100svh] w-full max-w-[1440px] flex-col items-center px-[20px] pb-[64px] pt-[55px] lg:px-[32px] lg:pb-[96px] lg:pt-[51px]">
        <header className="flex w-full max-w-[1060px] flex-col items-center gap-y-[12px] text-center lg:gap-y-[13px]">
          <h1 className="max-w-[336px] font-melun text-[36px] font-bold leading-[1.2] lg:max-w-none lg:whitespace-nowrap lg:text-[70px]">
            Ready for the full experience ?
          </h1>
          <p className="max-w-[336px] font-ginto text-[18px] leading-[1.3] lg:max-w-[1050px] lg:text-[21px]">
            Dojo Sparks gives your child the power of 1-on-1 reading coaching at home — all for less than $1 a day.
          </p>
        </header>

        <MobilePricing annualHref={annualHref} monthlyHref={monthlyHref} />
        <DesktopPricing annualHref={annualHref} monthlyHref={monthlyHref} />
      </div>
    </main>
  );
};
